import React, { useEffect, useState } from "react";
import { db } from '../../../firebase';
import { collection, getDocs, doc, getDoc, updateDoc, onSnapshot  } from "firebase/firestore";
import Table from "./Table";
import NavBars from '../../Sidebar/Sidebar';
import { waitFor } from "@testing-library/react";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [editedCells, setEditedCells] = useState(new Set()); // Track edited cells

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const userData = await Promise.all(
          querySnapshot.docs.map(async (docSnapshot) => {
            const user = docSnapshot.data();

            const departmentRef = user["sub-unit"] ? await getDoc(user["sub-unit"]) : null;
            const roleRef = user.role ? await getDoc(user.role) : null;

            return {
              id: docSnapshot.id,
              name: user.name,
              email: user.email,
              joined: user.joined ? new Date(user.joined).toLocaleString() : "N/A",
              department: departmentRef ? departmentRef.data()?.name : "N/A",
              role: roleRef ? roleRef.data()?.name : "N/A",
              departmentId: user["sub-unit"]?.id || "",
              roleId: user.role?.id || "",
            };
          })
        );
        setUsers(userData);
        setFilteredUsers(userData);
        setEditedCells(new Set()); // Reset edits when fetching new data
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchDepartmentsAndRoles = async () => {
      try {
        const deptSnapshot = await getDocs(collection(db, "sub-units"));
        setDepartments(deptSnapshot.docs.map((doc) => ({ id: doc.id, name: doc.data().name })));

        const roleSnapshot = await getDocs(collection(db, "roles"));
        setRoles(roleSnapshot.docs.map((doc) => ({ id: doc.id, name: doc.data().name })));
      } catch (error) {
        console.error("Error fetching departments and roles:", error);
      }
    };

    fetchUsers();
    fetchDepartmentsAndRoles();
  }, []);

  const updateUserField = async (userId, field, value) => {
    try {
      const userRef = doc(db, "users", userId);
      const updatedValue = value ? doc(db, field === "sub-unit" ? "sub-units" : "roles", value) : null;
      let updateData = { [field]: updatedValue };
  
      // Handle role-specific updates
      if (field === "role") {
        if (value === "Wz2LDaD8fZ1hNjyS2NWx") {
          updateData.isAdmin = true;
          updateData.isLeader = true;
        } else if (value === "cfYTTtevPHwefj1qG0c3") {
          updateData.isAdmin = false;
          updateData.isLeader = true;
        } else if (value === "QvYgWCwCboltlCFHyhve") {
          updateData.isAdmin = false;
          updateData.isLeader = false;
        }
      }
  
      // Update the user document in Firestore
      await updateDoc(userRef, updateData);
  
      // Listen for Firestore update
      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(userRef, async (updatedUserSnap) => {
          if (updatedUserSnap.exists()) {
            unsubscribe(); // Stop listening once we get the updated document
  
            const updatedUser = updatedUserSnap.data();
            const subUnitRef = updatedUser["sub-unit"] ? await getDoc(updatedUser["sub-unit"]) : null;
            const roleRef = updatedUser.role ? await getDoc(updatedUser.role) : null;
  
            // Update local state immediately
            setUsers((prevUsers) =>
              prevUsers.map((user) =>
                user.id === userId
                  ? {
                      ...user,
                      departmentId: updatedUser["sub-unit"]?.id || "",
                      roleId: updatedUser.role?.id || "",
                      department: subUnitRef ? subUnitRef.data()?.name : "N/A",
                      role: roleRef ? roleRef.data()?.name : "N/A",
                      isAdmin: updatedUser.isAdmin || false, 
                    }
                  : user
              )
            );
  
            // Apply filters after update
            await applyFilters(selectedDepartment, selectedRole);
  
            // Mark cell as edited
            setEditedCells((prev) => new Set(prev).add(`${userId}-${field}`));
  
            resolve(); // Resolve the promise after completion
          }
        }, reject);
      });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
    }
  };
  
  const applyFilters = (deptFilter, roleFilter) => {
    let filtered = users;
    if (deptFilter) {
      filtered = filtered.filter(user => user.departmentId === deptFilter);
    }
    if (roleFilter) {
      filtered = filtered.filter(user => user.roleId === roleFilter);
    }
    setFilteredUsers(filtered);
  };

  const handleFilterChange = (field, value) => {
    if (field === "sub-unit") {
      setSelectedDepartment(value);
      applyFilters(value, selectedRole);
    } else if (field === "role") {
      setSelectedRole(value);
      applyFilters(selectedDepartment, value);
    }
  };

  const columns = ["Name", "Email", "Department", "Role"];
  const columnTypes = ["text", "text", "dropdown", "dropdown"];

  const managementSubUnitIds = ["nldLZalGeVwPb2HJiGcr", "OTx6hWrSa2Hd56NB4T2X"];

  // Get the current user's sub-unit from localStorage
  const storedSubUnit = localStorage.getItem("subUnit")?.replace(/"/g, ""); // Remove any accidental quotes

  const isManagement = managementSubUnitIds.includes(storedSubUnit); // Check if it's a management sub-unit
  
  const tableData = filteredUsers.map((user) => [
    user.name,
    user.email,
    <select
      value={user.departmentId}
      onChange={(e) => updateUserField(user.id, "sub-unit", e.target.value)}
      disabled={!isManagement} // Disable if not in Management
      style={{
        backgroundColor: editedCells.has(`${user.id}-sub-unit`) ? "lightgreen" : "white",
      }}
    >
      <option value="">Select Department</option>
      {departments.map((dept) => (
        <option key={dept.id} value={dept.id}>{dept.name}</option>
      ))}
    </select>,

    <select
      value={user.roleId}
      onChange={(e) => updateUserField(user.id, "role", e.target.value)}
      disabled={!isManagement} // Disable if not in Management
      style={{
        backgroundColor: editedCells.has(`${user.id}-role`) ? "lightgreen" : "white",
      }}
    >
      <option value="">Select Role</option>
      {roles.map((role) => (
        <option key={role.id} value={role.id}>{role.name}</option>
      ))}
    </select>
  ]);

  return (
    <div>
      <div className="content">
        <h2>Manage Users</h2>

        {/* Filter Section */}
        <div className="filters">
          <label>
            Filter by Department: 
            <select value={selectedDepartment} onChange={(e) => handleFilterChange("sub-unit", e.target.value)}>
              <option value="">All</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </label>

          <label>
            Filter by Role: 
            <select value={selectedRole} onChange={(e) => handleFilterChange("role", e.target.value)}>
              <option value="">All</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Table Component */}
        <Table 
          rows={filteredUsers.length} 
          columns={columns.length} 
          columnNames={columns} 
          columnTypes={columnTypes} 
          data={tableData} 
          departments={departments} 
          roles={roles} 
          onDropdownChange={(rowIndex, colIndex, value) => {
            const field = colIndex === 2 ? "sub-unit" : colIndex === 3 ? "role" : "";
            if (field) {
              updateUserField(filteredUsers[rowIndex].id, field, value);

            }
          }}
        />
      </div>
      <NavBars/>
    </div>
  );
};

export default ManageUsers;
